import React, { useState } from "react";
import { motion } from "framer-motion";
import { CalendarCheck, Loader2, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";
import { gaEvent, trackBrokerConversion } from "../../lib/ga";

const TIMELINES = ["1–3 months", "3–6 months", "Just curious"];

export default function CMALeadCard({ address, estimate }) {
  const [form, setForm] = useState({ name: "", email: "", phone: "", timeline: "" });
  const [company, setCompany] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const valid = form.name.trim() && form.email.trim() && form.phone.trim() && form.timeline;

  const submit = async (e) => {
    e.preventDefault();
    if (!valid || loading) return;
    setLoading(true);
    try {
      await api.post("/leads/cma", { ...form, company, address: address || null, estimate: estimate ? String(estimate) : null });
      gaEvent("submit_cma_lead", { timeline: form.timeline });
      trackBrokerConversion();
      setDone(true);
      toast.success("Request sent — Louis Fong will be in touch shortly.");
    } catch {
      toast.error("Something went wrong. Please call (408) 561-1321.");
    } finally { setLoading(false); }
  };

  return (
    <motion.div initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5 }}
      className="rounded-2xl bg-brand-emerald text-brand-cream p-6 sm:p-8 shadow-[0_10px_40px_rgba(11,30,140,0.10)]" data-testid="cma-lead-card">
      <div className="flex items-center gap-2">
        <CalendarCheck className="h-5 w-5 text-brand-gold" />
        <span className="text-xs uppercase tracking-[0.2em] text-brand-cream/60">Verified In-Person CMA</span>
      </div>
      <h2 className="mt-2 font-serif text-2xl sm:text-3xl">Want a verified, in-person Comparative Market Analysis?</h2>
      <p className="mt-2 text-sm text-brand-cream/75 max-w-2xl">
        An automated estimate is a starting point. Get a precise, broker-reviewed CMA from an Equitas Residential advisor — Louis Fong (DRE 01151598) — tailored to your home and street.
      </p>

      {done ? (
        <div className="mt-6 flex items-center gap-3 rounded-xl bg-brand-cream/10 p-5" data-testid="cma-success">
          <CheckCircle2 className="h-6 w-6 text-brand-gold shrink-0" />
          <p className="text-sm text-brand-cream/90">Thank you — your request is in. Louis Fong will reach out shortly to schedule your complimentary CMA.</p>
        </div>
      ) : (
        <form onSubmit={submit} className="mt-6 space-y-4">
          <input type="text" name="company" tabIndex={-1} autoComplete="off" aria-hidden="true"
            value={company} onChange={(e) => setCompany(e.target.value)} data-testid="cma-company-honeypot"
            style={{ position: "absolute", left: "-9999px", width: "1px", height: "1px", opacity: 0 }} />
          <div className="grid sm:grid-cols-3 gap-3">
            <input data-testid="cma-name-input" value={form.name} onChange={set("name")} placeholder="Full name" autoComplete="name"
              className="rounded-lg bg-brand-cream/10 border border-brand-cream/20 px-4 py-3 text-sm text-brand-cream placeholder:text-brand-cream/50 outline-none focus:ring-1 focus:ring-brand-gold" />
            <input data-testid="cma-email-input" type="email" value={form.email} onChange={set("email")} placeholder="Email" autoComplete="email"
              className="rounded-lg bg-brand-cream/10 border border-brand-cream/20 px-4 py-3 text-sm text-brand-cream placeholder:text-brand-cream/50 outline-none focus:ring-1 focus:ring-brand-gold" />
            <input data-testid="cma-phone-input" value={form.phone} onChange={set("phone")} placeholder="Phone number" autoComplete="tel"
              className="rounded-lg bg-brand-cream/10 border border-brand-cream/20 px-4 py-3 text-sm text-brand-cream placeholder:text-brand-cream/50 outline-none focus:ring-1 focus:ring-brand-gold" />
          </div>
          <div>
            <p className="text-xs uppercase tracking-wider text-brand-cream/60 mb-2">Are you planning to sell?</p>
            <div className="flex flex-wrap gap-2">
              {TIMELINES.map((t) => (
                <button type="button" key={t} data-testid={`cma-timeline-${t.includes("1") ? "1-3" : t.includes("3–6") ? "3-6" : "curious"}`}
                  onClick={() => setForm((f) => ({ ...f, timeline: t }))}
                  className={`rounded-full px-4 py-2 text-sm font-medium transition-colors border ${
                    form.timeline === t
                      ? "bg-brand-gold text-brand-dark border-brand-gold"
                      : "bg-transparent text-brand-cream/80 border-brand-cream/25 hover:border-brand-gold"}`}>
                  {t}
                </button>
              ))}
            </div>
          </div>
          <button type="submit" disabled={!valid || loading} data-testid="cma-submit-button"
            className="inline-flex items-center justify-center gap-2 rounded-xl bg-brand-gold px-7 py-3.5 font-semibold text-brand-dark hover:brightness-95 transition-all disabled:opacity-50 disabled:cursor-not-allowed">
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <CalendarCheck className="h-4 w-4" />}
            Request my complimentary CMA
          </button>
        </form>
      )}
    </motion.div>
  );
}
